import { computed, ref, watch } from "vue";
import { useLocalStorage } from "@vueuse/core";
import { defineStore } from "pinia";
import Swal from "sweetalert2";

import { sortResponse } from "../Composables/OrderArticlesResponse";
import { Articulo } from "../../../interface/rp-response/articles.response";
import { ArticuloOrden } from "../../../interface";




const Toast = Swal.mixin({
    toast: true,
    position: "top-end",
    showConfirmButton: false,
    timer: 6000,
    customClass: {
        title: 'text-sm font-medium text-gray-900'
    },
    background: 'rgb(163 163 163)'

});


export const useOrderStore = defineStore('order-articles', () => {

    //PROPERTIES
    const orderArticles = ref(useLocalStorage<ArticuloOrden[]>('articles-order', []));
    const articlesRPResponse = ref<Articulo[] | boolean>(false);


    //GETTERS
    const getOrderArticles = computed(() => orderArticles.value)
    const getRPResponse = computed(() => articlesRPResponse.value)


    const productosOrdenados = computed(() => {
        return sortResponse(articlesRPResponse.value)

    })


    // Funcion helper interna del store
    // Funcion helper interna del store
    const calcularStockDisponible = (articulo: Articulo | ArticuloOrden): number => {

        // Si el articulo es un combo (clasificacion 'C'), retornar un valor alto para permitir cualquier cantidad
        if (articulo.clasificacion === 'C') {
            return 9999;
        }
        return Math.max(0, articulo.canti_stock - articulo.canti_reser);
    };



    // Watch para controlar las cantidades agregadas al store
    watch(orderArticles, (articulos) => {
        articulos.forEach(articulo => {
            const realStock = calcularStockDisponible(articulo);
            if (articulo.cantidad > realStock) {
                Toast.fire({
                    title: `La cantidad (${articulo.cantidad}) supera el stock disponible (${realStock}) para el artículo ${articulo.COD_PROVEEDOR}. Se ha ajustado automáticamente.`
                });
                articulo.cantidad = realStock;
            }
        });
    }, { deep: true });







    // ACTIONS
    const agregarArticulo = (articulo: ArticuloOrden) => {
        const availableStock = calcularStockDisponible(articulo);

        // Si es un combo, no necesitamos validar stock
        if (articulo.clasificacion !== 'C') {
            // Lógica existente para artículos normales
            if (articulo.cantidad > availableStock) {
                Toast.fire({
                    title: `La cantidad solicitada (${articulo.cantidad}) supera el stock disponible (${availableStock}) para el artículo ${articulo.COD_PROVEEDOR}. Se ha ajustado automáticamente.`
                });

                articulo.cantidad = availableStock;
            }
        }

        const findArticleInStore = getOrderArticles.value.find(product => product.cod_articulo === articulo.cod_articulo);

        if (findArticleInStore) {
            if (articulo.clasificacion === 'C') {
                // Para combos, simplemente sumamos las cantidades sin validar stock
                findArticleInStore.cantidad += articulo.cantidad;
            } else {
                // Para artículos normales, aplicamos la validación existente
                const newQuantity = findArticleInStore.cantidad + articulo.cantidad;
                if (newQuantity > availableStock) {
                    Toast.fire({
                        title: `La cantidad total (${newQuantity}) superaría el stock disponible (${availableStock}) para el artículo ${articulo.COD_PROVEEDOR}. Se ha ajustado automáticamente.`
                    });
                    findArticleInStore.cantidad = availableStock;
                } else {
                    findArticleInStore.cantidad = newQuantity;
                }
            }
        } else {
            orderArticles.value.push({
                ...articulo,
                cantidad: articulo.cantidad
            });
        }
    }

    const validateAllArticleQuantities = () => {
        orderArticles.value = orderArticles.value.map(articulo => {

            // Los combos no tienen restricciones de stock
            if(articulo.clasificacion === 'C') {
                return articulo;
            }

            const availableStock = calcularStockDisponible(articulo)
            articulo.cantidad = Math.min(articulo.cantidad, availableStock)
            return articulo;
        })
    }

    const clearArticlesOrder = () => {
        localStorage.removeItem('articles-order')
        orderArticles.value = []
    }



    const updateOrderStore = (newOrder: ArticuloOrden[]) => {
        orderArticles.value = [...newOrder]
    }

    const updatearticlesRPResponse = (responseRP: Articulo[] | boolean) => {
        articlesRPResponse.value = responseRP
    }


    return {
        //PROPERTIES
        articlesRPResponse,

        //GETTERS
        getRPResponse,
        productosOrdenados,
        getOrderArticles,


        //ACTIONS
        calcularStockDisponible,
        agregarArticulo,
        validateAllArticleQuantities,
        clearArticlesOrder,
        updateOrderStore,
        updatearticlesRPResponse,
    }
})